import { Dl, Dt, FormStatus, H3, Skeleton } from '@dnb/eufemia';
import { Field, Form, Value } from '@dnb/eufemia/extensions/forms';
import { useMedia } from '@dnb/eufemia/shared';
import type { ApiWithStatusAndScopesDto } from '@portals/shared/portal/ApiDto';
import { useEffect, useRef, useState } from 'react';

import { updateApiLifeCycle } from '@/api/api';
import { useBackend } from '@/hooks/useBackend';
import { ApiLifeCycleRequest } from '@/openapi/schema';
import FormSubmitButton from '@/pages/profile/apps/application/ApiProviderPage/components/FormSubmitButton';
import { apiLifecycleSchema } from '@/schemas/apiLifecycleSchema';
import { SwitchContainerMode } from '@/types/SwitchContainerModeTypes';

import { FormEditButton } from '../../../components/FormEditButton';
import ApiStageTimeLine, { STAGE_OPTIONS, STAGE_ORDER } from './ApiStageTimeLine';

type ApiStage = ApiLifeCycleRequest['stage'];
export type ApiStageTimeLineFormData = Partial<ApiLifeCycleRequest>;

export type ApiStageTimeLineFormProps = {
  api: ApiWithStatusAndScopesDto;
  isTeamAdmin: boolean;
  isApiChampion: boolean;
  onStageChange: (stage: ApiStage) => void;
};

export default function ApiStageTimeLineForm({
  api,
  isTeamAdmin,
  isApiChampion,
  onStageChange,
}: ApiStageTimeLineFormProps) {
  const { isSmall } = useMedia();

  const canEdit = isTeamAdmin && isApiChampion;
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const switchContainerModeRef = useRef<SwitchContainerMode>(undefined);

  const stagesRequiringMessage = new Set<ApiStage>(['DEPRECATED', 'DECOMMISSIONED']);

  const formId = `api-stage-timeline-${api.slug}`;
  const { data, update, set } = Form.useData<ApiStageTimeLineFormData>(formId);
  const formPath = '/stage';

  const {
    data: lifecycle,
    isLoading: lifecycleIsLoading,
    mutate: mutateLifecycle,
  } = useBackend('/apis/{apiSlug}/life-cycle/stage', {
    params: { path: { apiSlug: api.slug } },
  });

  useEffect(() => {
    if (lifecycle) {
      set({ stage: lifecycle.stage, stageMessage: lifecycle.stageMessage });
    }
  }, [lifecycle, set]);

  const currentStage = lifecycle?.stage;

  const handleStartEdit = () => {
    setErrorMessage(null);
    setIsEditing(true);
    resetLocal();
  };

  const handleSelectStage = (stage: ApiStage) => {
    if (!canEdit || !isEditing || isSaving) return;
    update(formPath, stage);
  };

  const resetLocal = () => {
    update(formPath, currentStage);
    update('/stageMessage', lifecycle?.stageMessage);
  };
  const selectedStage = data?.stage;
  const hasChanged = selectedStage !== currentStage || data?.stageMessage != lifecycle?.stageMessage;

  const handleSave = async (formData: ApiStageTimeLineFormData) => {
    if (!hasChanged || isSaving || !currentStage) return;

    setErrorMessage(null);
    setIsSaving(true);

    try {
      const request = apiLifecycleSchema.parse(formData);
      const { data, error } = await updateApiLifeCycle(
        api.slug,
        stagesRequiringMessage.has(request.stage) ? request : { stage: request.stage, stageMessage: null },
      );
      if (error) {
        setErrorMessage(error.detail ?? 'Failed to update API stage');
        return;
      }
      if (data?.stage) {
        await mutateLifecycle({ stage: data.stage, stageMessage: data.stageMessage });
        onStageChange(data.stage);
      }
      setIsEditing(false);
      switchContainerModeRef.current?.('view');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update API stage');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <H3 bottom="small">API Stage</H3>
      <Skeleton show={lifecycleIsLoading}>
        <Form.Handler<ApiStageTimeLineFormData> id={formId} onSubmit={handleSave} schema={apiLifecycleSchema}>
          <Form.Section containerMode="view">
            <Form.Section.ViewContainer bottom="small" onEdit={() => setErrorMessage(null)}>
              {isSmall ? (
                <Dl top="medium">
                  <Dt>{currentStage ? STAGE_OPTIONS[currentStage] : ''}</Dt>
                </Dl>
              ) : (
                <>
                  <ApiStageTimeLine formId={formId} interactive={false} />
                  <Value.String label="Message" path="/stageMessage" top="medium" />
                </>
              )}
              <Form.Section.Toolbar>
                <FormEditButton
                  disabled={!canEdit}
                  disabledTooltip={
                    isTeamAdmin ? (
                      isApiChampion ? undefined : (
                        <>Only API Champions have access to edit</>
                      )
                    ) : (
                      <>Only team admins have access to edit</>
                    )
                  }
                  onEdit={handleStartEdit}
                  switchContainerModeRef={switchContainerModeRef}
                />
              </Form.Section.Toolbar>
            </Form.Section.ViewContainer>

            <Form.Section.EditContainer bottom="small">
              {isSmall ? (
                <Field.Selection
                  disabled={!canEdit || isSaving}
                  onChange={(value) => handleSelectStage(value as ApiStage)}
                  path={formPath}
                  top="medium"
                  variant="dropdown"
                >
                  {STAGE_ORDER.map((stage) => (
                    <Field.Option key={stage} title={STAGE_OPTIONS[stage]} value={stage} />
                  ))}
                </Field.Selection>
              ) : (
                <ApiStageTimeLine formId={formId} interactive={canEdit} isSaving={isSaving} />
              )}
              {selectedStage && (
                <Form.Visibility
                  keepInDOM // Needed so that stage message is still validated when not visible
                  visibleWhen={{ path: '/stage', hasValue: (value: ApiStage) => stagesRequiringMessage.has(value) }}
                >
                  <Field.String
                    disabled={isSaving}
                    label="Message (10-255 characters)"
                    maxLength={255}
                    minLength={10}
                    multiline
                    path="/stageMessage"
                    placeholder={`Provide a message for the consumers explaining why this API is ${STAGE_OPTIONS[selectedStage].toLowerCase()} and information about possible alternatives`}
                    required={stagesRequiringMessage.has(selectedStage)}
                    top="medium"
                    transformOut={(value) => value ?? null}
                    width="stretch"
                  />
                </Form.Visibility>
              )}
              <Form.Section.Toolbar>
                <FormSubmitButton disabled={!hasChanged || isSaving} switchContainerModeRef={switchContainerModeRef} />
                <Form.Section.EditContainer.CancelButton showConfirmDialog={false} />
              </Form.Section.Toolbar>
              {errorMessage && <FormStatus state="error" stretch text={errorMessage} top="small" />}
            </Form.Section.EditContainer>
          </Form.Section>
        </Form.Handler>
      </Skeleton>
    </>
  );
}
